import { createApp } from 'vue'
import ElementPlus from 'element-plus'
import * as ElementPlusIconsVue from '@element-plus/icons-vue'
import 'element-plus/dist/index.css'
import axios from 'axios'
import App from './App.vue'
import store from './store'
import router from './router'

// 创建axios实例
const app = createApp(App)

// 根据环境变量设置API基础URL
const API_BASE_URL = process.env.VUE_APP_API_BASE_URL || 'http://jx.wxkoc.com/'
const API_TARGET = process.env.VUE_APP_API_TARGET || 'http://jx.wxkoc.com/'
const IS_DEVELOPMENT = process.env.VUE_APP_ENV === 'development'

console.log('当前环境:', process.env.VUE_APP_ENV || 'development')
console.log('API基础URL:', API_BASE_URL)
console.log('API目标地址:', API_TARGET)

const http = axios.create({
  baseURL: API_BASE_URL, // 使用环境变量配置的baseURL
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json'
  }
})

// 挂载全局变量
app.config.globalProperties.$apiBaseUrl = API_BASE_URL
app.config.globalProperties.$apiTarget = API_TARGET
app.config.globalProperties.$isDevelopment = IS_DEVELOPMENT

// 请求拦截器
http.interceptors.request.use(config => {
  // 从localStorage获取token并添加到请求头
  const token = localStorage.getItem('token')
  if (token) {
    config.headers.token = token
  }
  return config
}, error => {
  return Promise.reject(error)
})

// 响应拦截器
http.interceptors.response.use(response => {
  // 检查响应类型是否为blob（文件下载）
  const contentType = response.headers['content-type']
  if (response.config.responseType === 'blob' || 
      (contentType && contentType.includes('application/vnd.ms-excel')) || 
      (contentType && contentType.includes('application/octet-stream'))) {
    // 对于文件下载，返回完整的response对象
    return response
  }
  // 对于普通JSON响应，返回response.data
  return response.data
}, error => {
  return Promise.reject(error)
})

app.use(ElementPlus)
// 注册所有Element Plus图标
for (const [key, component] of Object.entries(ElementPlusIconsVue)) {
  app.component(key, component)
}
app.use(store)
app.use(router)

// 挂载axios到Vue实例
app.config.globalProperties.$axios = http

// 添加全局文件下载函数
app.config.globalProperties.$downloadFile = async function(url, params = {}, filename = '') {
  try {
    // 首先请求导出API，获取文件信息
    const response = await http({
      url: url,
      method: 'GET',
      params: params
    })
    
    // 检查响应格式
    if (response.code === 1 && response.data && response.data.download_url) {
      // 新格式：JSON响应包含下载URL
      const originalDownloadUrl = response.data.download_url
      const downloadFilename = response.data.filename || filename
      
      // 根据环境决定下载方式
      if (this.$isDevelopment) {
        // 开发环境：使用代理下载（不打开新窗口）
        const proxyDownloadUrl = originalDownloadUrl.replace(this.$apiTarget, '/download/')
        const iframe = document.createElement('iframe')
        iframe.style.display = 'none'
        iframe.src = proxyDownloadUrl
        document.body.appendChild(iframe)

        const link = document.createElement('a')
        link.href = proxyDownloadUrl
        link.download = downloadFilename || 'download.xlsx'
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)

        setTimeout(() => {
          if (document.body.contains(iframe)) {
            document.body.removeChild(iframe)
          }
        }, 5000)
      } else {
        // 生产环境：直接下载（不打开新窗口）
        const iframe = document.createElement('iframe')
        iframe.style.display = 'none'
        iframe.src = originalDownloadUrl
        document.body.appendChild(iframe)

        const link = document.createElement('a')
        link.href = originalDownloadUrl
        link.download = downloadFilename || 'download.xlsx'
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)

        setTimeout(() => {
          if (document.body.contains(iframe)) {
            document.body.removeChild(iframe)
          }
        }, 5000)
      }
      
      return {
        success: true,
        message: response.msg || '文件导出成功，正在下载...',
        data: response.data,
        downloadUrl: originalDownloadUrl
      }
    } else if (response.data instanceof Blob || 
               (response.headers && response.headers['content-type'] && 
                (response.headers['content-type'].includes('application/vnd.ms-excel') || 
                 response.headers['content-type'].includes('application/octet-stream')))) {
      // 旧格式：直接返回文件流
      const blob = new Blob([response.data])
      const blobUrl = window.URL.createObjectURL(blob)
      
      // 创建下载链接
      const link = document.createElement('a')
      link.href = blobUrl
      
      // 从响应头获取文件名，如果没有则使用传入的文件名
      let downloadFilename = filename
      const contentDisposition = response.headers['content-disposition']
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/)
        if (filenameMatch && filenameMatch[1]) {
          downloadFilename = filenameMatch[1].replace(/['"]/g, '')
        }
      }
      
      link.download = downloadFilename
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      
      // 释放blob URL
      window.URL.revokeObjectURL(blobUrl)
      
      return {
        success: true,
        message: '文件下载成功'
      }
    } else {
      // 未知响应格式
      throw new Error('未知的响应格式')
    }
  } catch (error) {
    console.error('文件下载失败:', error)
    return {
      success: false,
      message: error.message || '文件下载失败'
    }
  }
}

// 添加简单直接的下载函数
app.config.globalProperties.$directDownload = function(url) {
  // 方法1：直接使用window.open（最简单）
  window.open(url, '_blank')
  
  // 方法2：根据环境使用不同的下载方式
  if (this.$isDevelopment) {
    // 开发环境：使用代理
    const proxyUrl = url.replace(this.$apiTarget, '/download/')
    const form = document.createElement('form')
    form.method = 'GET'
    form.action = proxyUrl
    form.target = '_blank'
    form.style.display = 'none'
    document.body.appendChild(form)
    form.submit()
    document.body.removeChild(form)
  } else {
    // 生产环境：直接打开
    window.open(url, '_blank')
  }
  
  return true
}

app.mount('#app')
